<template>
  <div class="">
    <!-- Header -->
    <div class="sm:flex sm:items-center mb-4">
      <div class="sm:flex-auto">
        <h1 class="text-xl font-semibold leading-6 text-gray-900">Agents</h1>
        <p class="mt-1 text-sm text-gray-500">Manage all registered agents and their accounts</p>
      </div>
      <div class="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
        <router-link
          to="/agents/add"
          class="inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
        >
          <i class="bx bx-plus mr-1"></i>
          New Agent
        </router-link>
      </div>
    </div>

    <!-- Filters Section -->
    <div class="bg-white border border-gray-200 rounded-lg mb-4 shadow-sm">
      <div 
        @click="showFilters = !showFilters"
        class="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
      >
        <h3 class="text-sm font-medium text-gray-700 flex items-center">
          <i class="bx bx-filter-alt mr-1"></i> Filters
          <span v-if="activeFilterCount > 0" class="ml-2 px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded-full">
            {{ activeFilterCount }} active
          </span>
        </h3>
        <div class="flex items-center space-x-2">
          <button 
            v-if="showFilters"
            @click.stop="clearFilters" 
            class="text-xs text-gray-500 hover:text-gray-700 flex items-center"
          >
            <i class="bx bx-x mr-1"></i> Clear All
          </button>
          <i :class="showFilters ? 'bx bx-chevron-up' : 'bx bx-chevron-down'" class="text-gray-500 text-lg"></i>
        </div>
      </div>
      <div v-show="showFilters" class="px-4 pb-4">
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <!-- Search -->
          <div class="lg:col-span-2">
            <label class="block text-xs font-medium text-gray-600 mb-1">Search</label>
            <input 
              type="text" 
              v-model="filter"
              @keyup.enter="applyFilters"
              placeholder="Search by name, contact..."
              class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <!-- Status Filter -->
          <div>
            <label class="block text-xs font-medium text-gray-600 mb-1">Status</label>
            <select 
              v-model="filters.status"
              class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <!-- Apply Button -->
          <div class="flex items-end">
            <button 
              @click="applyFilters"
              :disabled="loading"
              class="w-full px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              <i class="bx bx-search mr-1"></i> Apply Filters
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Results Info & Per Page -->
    <div class="flex items-center justify-between mb-3">
      <div class="text-sm text-gray-600" v-if="agents && agents.meta">
        Showing {{ agents.meta.from || 0 }} to {{ agents.meta.to || 0 }} of {{ agents.meta.total || 0 }} agents
      </div>
      <div class="flex items-center space-x-2">
        <label class="text-sm text-gray-600">Per page:</label>
        <select 
          v-model="perPage" 
          @change="applyFilters"
          class="px-2 py-1 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
        >
          <option :value="10">10</option>
          <option :value="15">15</option>
          <option :value="25">25</option>
          <option :value="50">50</option>
          <option :value="100">100</option>
        </select>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center items-center py-12">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      <span class="ml-2 text-gray-600">Loading agents...</span>
    </div>

    <div v-else class="flex flex-col">
      <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
          <div class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Name
                  </th>
                  <th
                    scope="col"
                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Balance
                  </th>
                  <th
                    scope="col"
                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Status
                  </th>

                  <th scope="col" class="relative px-6 py-3">
                    <span class="sr-only">Edit</span>
                  </th>
                </tr>
              </thead>
              <tbody v-if="agents" class="bg-white divide-y divide-gray-200">
                <tr v-for="(agent, i) in agents.data" :key="i">
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="flex items-center">
                      <div class="flex-shrink-0 h-10 w-10">
                        <div
                          class="h-10 w-10 rounded-full bg-gray-200 flex justify-center items-center"
                        >
                          {{ agent.entity_name[0] }}
                        </div>
                      </div>
                      <div class="ml-4">
                        <div class="text-sm font-medium text-gray-900">
                          {{ agent.entity_name }}
                        </div>
                        <div class="text-sm text-gray-500">
                          {{ agent.contact_person }} /
                          {{ agent.contact_person_number }}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="text-sm text-gray-900">GMD {{ agent.balance }}</div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span
                      class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800"
                    >
                      {{ agent.status ? "active" : "in-active" }}
                    </span>
                  </td>

                  <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div class="flex justify-end">
                      <button
                        @click="onDropDown(i)"
                        class="relative z-10 block rounded-md bg-white text-xl p-2 focus:outline-none"
                      >
                        <i class="bx bx-dots-vertical-rounded"></i>
                      </button>
                    </div>
                    <transition
                      enter-active-class="transition ease-out duration-100"
                      enter-from-class="transform opacity-0 scale-95"
                      enter-to-class="transform opacity-100 scale-100"
                      leave-active-class="transition ease-in duration-75"
                      leave-from-class="transform opacity-100 scale-100"
                      leave-to-class="transform opacity-0 scale-95"
                    >
                      <div
                        v-show="dropdownOpen.isOpen && dropdownOpen.index == i"
                        @click="onDropDown(i)"
                        class="absolute right-10 mt-2 py-2 w-48 bg-white rounded-md shadow-xl z-20"
                      >
                        <router-link
                          :to="{
                            path: `/agents/${agent.id}`,
                          }"
                          class="block px-4 py-2 text-sm capitalize text-gray-700 hover:bg-blue-500 hover:text-white"
                          >View
                        </router-link>
                        <router-link
                          :to="{
                            path: `/agents/${agent.id}/edit`,
                          }"
                          class="block px-4 py-2 text-sm capitalize text-gray-700 hover:bg-blue-500 hover:text-white"
                          >Edit
                        </router-link>
                        <button
                          @click="handleBlock(agent.id)"
                          class="block px-4 font-bold w-full py-2 text-right text-sm text-red-600 capitalize hover:bg-blue-500 hover:text-white"
                        >
                          {{ agent.status ? "Block" : "Unblock" }}
                        </button>
                        <button
                          @click="showConfirmModal(agent)"
                          class="block px-4 font-bold w-full py-2 text-right text-sm text-red-600 capitalize hover:bg-blue-500 hover:text-white"
                        >
                          Reset Password
                        </button>
                      </div>
                    </transition>
                  </td>
                </tr>

                <!-- More people... -->
              </tbody>
            </table>

            <!-- Empty State -->
            <div v-if="agents && agents.data && agents.data.length === 0" class="text-center py-12">
              <i class="bx bx-user-x text-4xl text-gray-400"></i>
              <p class="mt-2 text-gray-500">No agents found</p>
              <p class="text-sm text-gray-400">Try adjusting your filters or add a new agent</p>
            </div>

            <div class="border-t" v-if="agents && agents.data && agents.data.length > 0">
              <pagination :data="agents" :midSize="2" @clicked="loadData"> </pagination>
            </div>

            <Confimation
              v-show="isConfirmModalVisible"
              modalHeadline="Please confirm?"
              :isLoading="isLoading"
              :confirmMessage="getMessage()"
              @confirmEvent="confirmRequest"
              @close="closeConfirmModal"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";
import { mapGetters } from "vuex";
import Pagination from "../../components/Pagination";
import Confimation from "../../components/Confimation.vue";
import checkPermissionExist from "../../mixins/checkPermissionExist";

export default {
  middleware: "auth",
  components: { Pagination, Confimation },

  layout: "default",

  mixins: [checkPermissionExist],

  computed: {
    ...mapGetters({
      user: "auth/user",
    }),
    activeFilterCount() {
      let count = 0;
      if (this.filter) count++;
      if (this.filters.status) count++;
      return count;
    },
  },

  data: () => ({
    agents: [],
    isLoading: false,
    loading: false,
    filter: "",
    onFilter: false,
    filterAgents: "",
    filterDate: "",
    searchStaffs: [],
    showFilters: false,
    perPage: 15,
    filters: {
      status: '',
    },

    selectedAgent: null,
    isConfirmModalVisible: false,
    dropdownOpen: {
      isOpen: false,
      index: null,
    },
    userHasConfirmed: false,
    modalType: "",
  }),

  created() {
    var _this = this;
    _this.loadData(1);
    if (!this.checkPermissionExist(this.user.data, "agents-read")) {
      this.$router.go(-1);
      return false;
    }
  },

  methods: {
    search() {
      var vm = this;
      this.searching = true;
      vm.loadData(1);
    },

    applyFilters() {
      this.loadData(1);
    },

    clearFilters() {
      this.filter = '';
      this.filters = {
        status: '',
      };
      this.loadData(1);
    },

    rqFilter() {
      this.onFilter = !this.onFilter;

      if (this.onFilter) {
        this.loadBranches();
        this.loadStaffs();
      }
    },

    getMessage() {
      if (this.selectedAgent) {
        return `Are you sure you want to reset ${this.selectedAgent.entity_name}'s password`;
      }

      return "";
    },

    buildQueryParams(pageNo) {
      const params = new URLSearchParams();
      params.append('page', pageNo);
      params.append('per_page', this.perPage);
      
      if (this.filter) {
        params.append('filter', this.filter);
      }
      if (this.filters.status) {
        params.append('status', this.filters.status);
      }
      
      return params.toString();
    },

    async loadData(pageNo) {
      this.loading = true;

      try {
        const queryString = this.buildQueryParams(pageNo);
        const { data } = await axios.get(`/agents?${queryString}`);

        this.agents = data;
        this.loading = false;

        return true;
      } catch (e) {
        console.log("Error loading agents:", e);
        this.loading = false;
        return false;
      }
    },

    async handleBlock(id) {
      console.log("id", id);
      const agent_id = id;
      try {
        const { data } = await axios.patch(`agent/${agent_id}/block`, {
          agent_id,
        });
        if (data) {
          console.log("response data", data);
          this.loadData(1);
        }
      } catch (error) {
        console.error("error in blocking agent", error);
      }
    },

    onDropDown(i) {
      if (this.dropdownOpen.isOpen == false) {
        this.dropdownOpen.isOpen = true;
        this.dropdownOpen.index = i;

        return;
      }

      this.dropdownOpen.isOpen = false;
      this.dropdownOpen.index = null;
    },

    showConfirmModal(agent) {
      console.log("Agent", agent);
      this.selectedAgent = agent;
      // this.modalType = type;
      this.isConfirmModalVisible = true;
    },

    closeConfirmModal() {
      this.selectedAgent = null;
      this.isConfirmModalVisible = false;
    },

    confirmRequest(req) {
      console.log("User has confirmed", req);
      this.userHasConfirmed = req;

      this.resetPassword();
    },

    async resetPassword() {
      // Submit the form
      this.isLoading = true;

      try {
        await axios.patch(`/agent/update/password?agent_id=${this.selectedAgent.id}`);

        this.isLoading = false;

        this.loadData(1);
        this.closeConfirmModal();

        Swal.fire({
          icon: "success",
          title: "Password Successfully Rest",
        });

        return true;
      } catch (e) {
        this.isLoading = false;

        console.log("Error");
        return false;
      }
    },
  },
};
</script>
